import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  cropConnectApiClient,
  BusinessPartnerDto,
  AgricenterWithConsultantListDto,
} from '../services/cropConnectApiClient';

interface ApiError {
  message: string;
  code?: string;
}

interface BpState {
  businessPartnerMetadata: BusinessPartnerDto | null;
  agriCenterNumber: string | null;
  consultants: AgricenterWithConsultantListDto | null;
  isLoading: boolean;
  error: ApiError | null;
  fetchBusinessPartnerMetadata: () => Promise<void>;
  fetchConsultantsByFactory: (factoryId?: string) => Promise<void>;
}

export const useBpStore = create<BpState>()(
  devtools((set, get) => ({
    businessPartnerMetadata: null,
    agriCenterNumber: null,
    consultants: null,
    isLoading: false,
    error: null,

    fetchBusinessPartnerMetadata: async () => {
      set({ isLoading: true, error: null });
      try {
        const response =
          await cropConnectApiClient.getCurrentBusinessPartnerMetadata();
        if (response.success) {
          const metadata = response.data;
          set({
            businessPartnerMetadata: metadata,
            agriCenterNumber: metadata?.agriCenterNumber,
            isLoading: false,
          });
        } else {
          set({
            error: { message: 'Failed to fetch business partner metadata' },
            isLoading: false,
          });
        }
      } catch (err) {
        const error: ApiError = {
          message:
            err instanceof Error ? err.message : 'An unknown error occurred',
        };
        set({ error, isLoading: false });
      }
    },

    fetchConsultantsByFactory: async (agricenterId?: string) => {
      set({ isLoading: true, error: null });

      try {
        const { agriCenterNumber } = get();
        const finalAgricenterId = agricenterId || agriCenterNumber;

        if (finalAgricenterId) {
          const response =
            await cropConnectApiClient.getConsultantsByAgriCenter(
              finalAgricenterId
            );
          if (response.success) {
            set({ consultants: response.data, isLoading: false });
          } else {
            set({
              error: { message: 'Failed to fetch consultants by factory' },
              isLoading: false,
            });
          }
        } else {
          set({
            error: { message: 'Factory ID is missing' },
            isLoading: false,
          });
        }
      } catch (err) {
        const error: ApiError = {
          message:
            err instanceof Error ? err.message : 'An unknown error occurred',
        };
        set({ error, isLoading: false });
      }
    },
  }))
);
